'use client';

import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { ExtendedDbCloudWalletUserInfo } from '@shared/types/db/user';
import { toast } from 'sonner';
import ImageWithFallback from '@/components/shared/ImageWithFallback';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useUserCloudQuery } from '@/hooks/onboarding/useReferralQuery';
import { _checkUsernameAvailability, _updateDbCloudInfo, StagesProfileForm, UserFormSchema } from '@/hooks/auth/userForm';
import SelectAvatar from './SelectAvatar';
import AvailableAvatars from './AvailableAvatars';
import { avatars } from '../SelectPfp';
export default function DesktopProfileForm({
  userInfo
}: {
  userInfo: {
    data: ExtendedDbCloudWalletUserInfo;
  };
}) {
  const [stage, setStage] = useState(StagesProfileForm.default);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const evenIndicies = avatars.filter((_, index) => index % 2 === 0);
  const avatar = evenIndicies[Math.floor(Math.random() * evenIndicies.length)];
  const [selected, setSelected] = useState(avatar);
  const {
    refetch: refetchUserCloudData
  } = useUserCloudQuery();
  const _onSubmitting = useMemo(() => stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount ? true : false, [stage]);
  const form = useForm<z.infer<typeof UserFormSchema>>({
    resolver: zodResolver(UserFormSchema),
    defaultValues: {
      pfp: selected.file,
      username: '',
      first_name: '',
      experience: 'new'
    }
  });
  const handleOnselectPfp = (_pfp: string) => {
    form.setValue('pfp', _pfp);
  };
  const handleUsernameInputChange = e => {
    const _inputValue = !e.target.value.startsWith('@') ? `@${e.target.value}` : e.target.value;
    form.setValue('username', _inputValue);
    form.setValue('first_name', _inputValue);
  };
  async function onSubmit(values: z.infer<typeof UserFormSchema>) {
    setStage(StagesProfileForm.isCheckingUsername);
    try {
      const _isUsernameAvailable = await _checkUsernameAvailability(values.username);
      if (!_isUsernameAvailable) {
        form.setError('username', {
          message: 'Username is not available, try another one.'
        }, {
          shouldFocus: true
        });
        setStage(StagesProfileForm.default);
        return;
      }
      setStage(StagesProfileForm.isUpdatingAccount);
      const _userInfo: ExtendedDbCloudWalletUserInfo = {
        ...userInfo.data,
        username: values.username,
        crypto_experience: values.experience,
        pfp: values.pfp,
        first_name: values.first_name
      };
      const _updateUser = await _updateDbCloudInfo(_userInfo);
      if (_updateUser.status === 201) {
        refetchUserCloudData();
      }
      if (_updateUser.data.error) {
        throw new Error('Error saving your profile');
      }
    } catch (error) {
      toast.error('Something went wrong, try again', {
        duration: 1000
      });
      setStage(StagesProfileForm.default);
      console.error('onSubmit:ERROR', {
        error
      });
    }
  }
  function toggleModal() {
    if (_onSubmitting) return;
    setIsModalOpen(!isModalOpen);
  }
  const handleSelect = avatar => {
    setSelected({
      file: avatar.file,
      name: avatar.name
    });
    handleOnselectPfp(avatar.file);
    toggleModal();
  };
  return <div className='h-fit overflow-y-auto' data-sentry-component="DesktopProfileForm" data-sentry-source-file="DesktopProfileForm.tsx">
      {!isModalOpen ? <div className='px-1 w-[356px]'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='grid gap-2 space-y-3'>
              {/* pfp */}
              <FormField control={form.control} name='pfp' render={() => <FormItem>
                    <FormLabel className='text-[16px] w-full flex justify-start mb-4'>
                      Choose Profile Picture
                    </FormLabel>

                    <FormControl>
                      <div className='flex flex-col items-center'>
                        <Label htmlFor='pfp' className='cursor-pointer'>
                          <SelectAvatar selected={selected} disabled={_onSubmitting} toggleModal={toggleModal} />
                        </Label>
                        <FormMessage />
                      </div>
                    </FormControl>
                  </FormItem>} />
              {/* username */}
              <FormField control={form.control} name='username' render={({
            field
          }) => <FormItem className='space-y-3'>
                    <FormLabel className='text-[16px]'>Choose Username</FormLabel>
                    <p className='text-[10px] text-genius-cream/50'>
                      Choose wisely, this is your final username. No take-backs.
                    </p>
                    <FormControl>
                      <Input {...field} className='bg-genius-blue border border-genius-blue focus:border-genius-pink transition-colors' disabled={_onSubmitting} placeholder='@SexyEinstein' onChange={handleUsernameInputChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              {/* first - last name */}
              {/* <div className='grid gap-2 w-full'>
                <FormLabel className='text-[16px]'>What should we call you?</FormLabel>
                <div className='gap-2 w-full'>
                  <FormField
                    control={form.control}
                    name='first_name'
                    render={({ field }) => (
                      <FormItem className='w-full'>
                        <FormControl className='w-full'>
                          <Input
                            className='bg-genius-blue border border-genius-blue focus:border-genius-pink transition-colors'
                            disabled={_onSubmitting}
                            placeholder='Nickname'
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
               </div> */}
              {/* experience */}
              <FormField control={form.control} name='experience' render={({
            field
          }) => <FormItem className='space-y-2'>
                    <FormLabel className='text-[16px]'>
                      How experienced are you with Crypto?
                    </FormLabel>
                    <FormControl>
                      <ToggleGroup disabled={Boolean(stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount)} type='single' value={field.value} onValueChange={value => {
                if (value) {
                  field.onChange(value);
                }
              }} className='grid grid-cols-1 gap-2'>
                        <ToggleGroupItem value='new' className='flex flex-col items-start h-[62px] p-4 mb-2  hover:bg-transaparent border-genius-blue border-[1px] rounded-lg data-[state=on]:bg-genius-blue'>
                          <span className='text-[14px]'>First Timer</span>
                          <span className='text-[12px] ta-l text-genius-cream/50'>
                            I've never traded crypto before
                          </span>
                        </ToggleGroupItem>
                        <ToggleGroupItem value='medium' className='flex flex-col items-start h-[62px] p-4 mb-2 border-genius-blue border-[1px] rounded-lg data-[state=on]:bg-genius-blue'>
                          <span className='text-[14px]'>Been around the block</span>
                          <span className='text-[12px] ta-l text-genius-cream/50'>
                            I own crypto on another platform but I'm no expert
                          </span>
                        </ToggleGroupItem>
                        <ToggleGroupItem value='experienced' className='flex flex-col items-start h-[62px] p-4 hover:bg-transaparent border-genius-blue border-[1px] rounded-lg data-[state=on]:bg-genius-blue'>
                          <div className='text-[14px] flex items-center'>
                            <ImageWithFallback src='/geniusImages/gen1.png' alt='genius' width={20} height={20} />
                            <p className='ml-1'>Degen</p>
                          </div>
                          <span className='text-[12px] ta-l text-genius-cream/50'>
                            I am a crypto God. I am also very humble.
                          </span>
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <Button className='h-[56px] rounded-full bg-genius-pink hover:bg-genius-pink mb-4' type='submit' disabled={_onSubmitting}>
                <div className='flex gap-2'>
                  {stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
                  {stage === StagesProfileForm.isCheckingUsername && <p>Checking Username</p>}
                  {stage === StagesProfileForm.isUpdatingAccount && <p>Saving Profile</p>}
                  {stage === StagesProfileForm.default && <p className=''>Continue</p>}
                </div>
              </Button>
            </form>
          </Form>
        </div> : <AvailableAvatars toggleModal={toggleModal} handleSelect={handleSelect} selected={selected} />}
    </div>;
}